import { useState, useCallback } from 'react';

export interface PaginationState {
  offset: number;
  limit: number;
  totalCount: number;
  totalPages: number;
  currentPage: number;
  fromVal: number;
  toVal: number;
}

export interface PaginationConfig {
  initialLimit?: number;
  initialOffset?: number;
}

export interface UsePaginationReturn {
  pagination: PaginationState;
  setPagination: (pagination: Partial<PaginationState>) => void;
  updatePaginationFromResponse: (response: any, pageOffset?: number) => void;
  handlePageChange: (event: any, value: number) => void;
  resetPagination: () => void;
  setLoading: (loading: boolean) => void;
  isLoading: boolean;
}

export const computePageWindow = (
  offset: number,
  limit: number,
  totalCount: number,
  pageSize: number
) => {
  const safeOffset = Number.isFinite(offset) && offset >= 0 ? offset : 0;
  const safeLimit = limit > 0 ? limit : 10;
  const totalPages = Math.ceil(totalCount / safeLimit) || 0;
  const currentPage = safeOffset + 1;
  const fromVal = totalCount !== 0 ? safeOffset * safeLimit + 1 : 0;
  const toVal =
    totalCount !== 0
      ? Math.min(safeOffset * safeLimit + pageSize, totalCount)
      : 0;
  return { offset: safeOffset, totalPages, currentPage, fromVal, toVal };
};

export const resolveResponsePageOffset = (
  payload: any,
  requestedOffset: number | undefined,
  previousOffset: number
): number => {
  if (
    typeof requestedOffset === "number" &&
    Number.isFinite(requestedOffset) &&
    requestedOffset >= 0
  ) {
    return requestedOffset;
  }
  const echoed = Number(payload?.offset);
  if (Number.isFinite(echoed) && echoed >= 0) {
    return echoed;
  }
  return previousOffset;
};

/**
 * Pagination for studio `Index` + product/catalogue-product APIs.
 * `offset` is a 0-based **page index** (page 1 → 0, page 2 → 1), not a skip count.
 * Do not multiply by `limit` here: `Index` displays `page = offset + 1`, and those APIs
 * use `skip(offset * limit)` / `PageRequest.of(offset, limit)`.
 */
export const usePagination = (config: PaginationConfig = {}): UsePaginationReturn => {
  const { initialLimit = 10, initialOffset = 0 } = config;
  
  const [pagination, setPaginationState] = useState<PaginationState>({
    offset: initialOffset,
    limit: initialLimit,
    totalCount: 0,
    totalPages: 0,
    currentPage: 1,
    fromVal: 0,
    toVal: 0,
  });

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const setPagination = useCallback((newPagination: Partial<PaginationState>) => {
    setPaginationState(prev => ({
      ...prev,
      ...newPagination,
    }));
  }, []);

  const updatePaginationFromResponse = useCallback((response: any, pageOffset?: number) => {
    if (!response?.data) {
      return;
    }
    const payload = response.data;
    const totalCount = Number(
      payload.pagination?.total ??
        payload.length ??
        payload.totalCount ??
        payload.total ??
        0
    ) || 0;
    const pageSize =
      payload.docs?.length || payload.products?.length || 0;

    setPaginationState((prev) => {
      const offset = resolveResponsePageOffset(payload, pageOffset, prev.offset);
      const window = computePageWindow(offset, prev.limit, totalCount, pageSize);
      return {
        ...prev,
        ...window,
        totalCount,
      };
    });
  }, []);

  const handlePageChange = useCallback((event: any, value: number) => {
    const page = Number(value);
    if (!Number.isFinite(page) || page < 1) {
      return;
    }
    const newOffset = page - 1;
    setPaginationState(prev => ({
      ...prev,
      offset: newOffset,
      currentPage: page,
    }));
  }, []);

  const resetPagination = useCallback(() => {
    setPaginationState(prev => ({
      ...prev,
      offset: initialOffset,
      currentPage: 1,
      totalCount: 0,
      totalPages: 0,
      fromVal: 0,
      toVal: 0,
    }));
  }, [initialOffset]);

  const setLoading = useCallback((loading: boolean) => {
    setIsLoading(loading);
  }, []);

  return {
    pagination,
    setPagination,
    updatePaginationFromResponse,
    handlePageChange,
    resetPagination,
    setLoading,
    isLoading,
  };
};

export default usePagination;
